import { createParser, createSerializer } from 'nuqs'
import { ClientCookiePersister } from '@/services/persister/ClientCookiePersister'
import { type PriceIntentAttribution, priceIntentAttributionSchema } from '@/services/priceIntent/priceIntent.types'
import { SHOP_SESSION_COOKIE_MAX_AGE } from '@/services/shopSession/ShopSession.constants'

const COOKIE_KEY = 'hedvig_price_intent_attribution_by_product'

const persister = new ClientCookiePersister(COOKIE_KEY, SHOP_SESSION_COOKIE_MAX_AGE)

export const PRICE_INTENT_ATTRIBUTION_USER_FLOW_QUERY_PARAM = 'attribution.userFlow' as const

export const EXTERNAL_INSURANCE_PRODUCT_RECOMMENDATION_USER_FLOW = 'EXTERNAL_INSURANCE_PRODUCT_RECOMMENDATION' as const

export const EXTERNAL_INSURANCE_OFFER_RECOMMENDATION_USER_FLOW = 'EXTERNAL_INSURANCE_OFFER_RECOMMENDATION' as const

export const parseAsPriceIntentAttributionUserFlow = createParser<string>({
  parse(value: string): string | null {
    const trimmedValue = value.trim()
    if (trimmedValue === '') return null
    return trimmedValue
  },
  serialize: (value) => value,
}).withOptions({ history: 'replace' })

const appendPriceIntentAttributionToSearch = createSerializer({
  [PRICE_INTENT_ATTRIBUTION_USER_FLOW_QUERY_PARAM]: parseAsPriceIntentAttributionUserFlow,
})

function readMap(): Record<string, PriceIntentAttribution> {
  try {
    const raw = persister.fetch()
    if (raw == null || raw === '') return {}
    const parsed: unknown = JSON.parse(raw)
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}

    return Object.fromEntries(
      Object.entries(parsed).flatMap(([productName, attribution]) => {
        const result = priceIntentAttributionSchema.safeParse(attribution)
        return result.success ? [[productName, result.data]] : []
      }),
    )
  } catch {
    return {}
  }
}

function writeMap(map: Record<string, PriceIntentAttribution>): void {
  if (Object.keys(map).length === 0) {
    persister.reset()
    return
  }
  persister.save(JSON.stringify(map))
}

export function markPriceIntentAttributionForProduct(productName: string, attribution: PriceIntentAttribution): void {
  const map = readMap()
  map[productName] = attribution
  writeMap(map)
}

export function getPriceIntentAttributionForProduct(productName: string): PriceIntentAttribution | null {
  return readMap()[productName] ?? null
}

export function clearPriceIntentAttributionForProduct(productName: string): void {
  const map = readMap()
  delete map[productName]
  writeMap(map)
}

export function appendPriceIntentAttributionQuery(href: string, attribution: PriceIntentAttribution): string {
  if (typeof window === 'undefined') return href

  const url = new URL(href, window.location.origin)
  const urlWithPriceIntentAttribution = appendPriceIntentAttributionToSearch(url, {
    [PRICE_INTENT_ATTRIBUTION_USER_FLOW_QUERY_PARAM]: attribution.userFlow ?? null,
  })
  const next = new URL(urlWithPriceIntentAttribution)
  return `${next.pathname}${next.search}${next.hash}`
}
